import { createSelector } from 'reselect';

import { getHash } from '../../utils/getHash';
import { getQuizConfig } from '../app-factory/selectors';

import { QuizStore, QuizStoreState, QuizList, Quiz, QuizEditorStoreState } from './typings';

const quizStoreSelector = (storeState: QuizStore): QuizStoreState => storeState.quiz;

const quizEditorStoreSelector = (storeState: QuizStore): QuizEditorStoreState => storeState.quizEditor;

export const getQuizToPlay = createSelector(quizEditorStoreSelector, (quizEditorStore): Quiz | null => {
    if (quizEditorStore.quizDraft && quizEditorStore.isQuizDraftStarted) {
        return quizEditorStore.quizDraft;
    }
    return null;
});

export const getQuizzes = createSelector(quizStoreSelector, (storeState) => storeState.quizzes);

export const getQuizzesLoadingStatus = createSelector(
    quizStoreSelector,
    (storeState) => storeState.quizzesLoadingStatus
);

export const getBunkerQuizLists = createSelector(getQuizConfig, (quizConfig) => {
    const groupedQuizzesBySubject: Record<string, QuizList> = {};

    for (const quizSubject of quizConfig.subjects) {
        const subjectSlug = quizSubject.slug;

        const quizList: QuizList = {
            title: quizSubject.title,
            cardImgSrcSet: quizSubject?.imgSrcSet,
            quizzes: [],
        };

        groupedQuizzesBySubject[subjectSlug] = quizList;
    }

    for (const quiz of quizConfig.quizzes) {
        const subjectSlug = quiz.subject;

        if (groupedQuizzesBySubject[subjectSlug] && quiz.questions.length) {
            groupedQuizzesBySubject[subjectSlug].quizzes.push({
                id: quiz.id || getHash(JSON.stringify(quiz)),
                ...quiz,
            });
        }
    }

    const subjects = Object.values(groupedQuizzesBySubject);

    return subjects.filter((subject) => Boolean(subject.quizzes.length));
});

export const getRoomPin = createSelector(quizStoreSelector, (storeState) => storeState.roomPin);

export const getStudentsMap = createSelector(quizStoreSelector, (storeState) => storeState.studentsMap);

const getStatData = createSelector(quizStoreSelector, (storeState) => storeState.statData);

export const getQuestionResults = createSelector(getStatData, (statData) => {
    if (statData?.results) {
        return Object.values(statData.results);
    }
    return null;
});

export const getMaxResult = createSelector(getQuestionResults, (questionResults) => {
    if (questionResults) {
        return Math.max(...questionResults);
    }
    return null;
});

export const getRightAnswerIndex = createSelector(getStatData, (statData) => {
    return statData?.rightAnswerIndex;
});
